import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { from, iif, Observable, of, throwError } from 'rxjs';
import { catchError, first, map, mergeMap, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth/auth.service';
import { CookieService } from 'ngx-cookie-service';
import { ModalLoginService } from '../services/modal-login/modal-login.service';
import { NavMenuService } from '../services/nav-menu/nav-menu.service';
import { RoutePermissionService } from '../services/routes-permission/routes-permission.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard  {

  constructor(
    private router: Router,
    private authService: AuthService,
    private cookieService: CookieService,
    private modalLoginService: ModalLoginService,
    private routePermission: RoutePermissionService
  ) {}

  canDeactivate() {
    this.authService.isFirstRun = false;
    return true;
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    this.authService.url = route.url;
    return this.authService.getOrgUser().pipe(
      mergeMap(orgUser => {
        if(orgUser.status === 4) {
          return throwError({
            error: {
              errorCode: "Blocked"
            }
          });
        }
        if(orgUser.status === 10) {
          return throwError({
            error: {
              errorCode: "Closed"
            }
          });
        }
        if(orgUser.status !== 0) {
          window.location.href = "/signup.html";
          return throwError({
            error: {
              errorCode: "Signup"
            }
          });
        }

        const routePermission = this.routePermission.findRoute(state.url) || {
          access: [1]
        };

        const { access } = orgUser.loggedInUser ? orgUser.loggedInUser: {
          access: [1]
        };
        const { length } = _.intersection(access, routePermission.access);
        if(length < 1) {
          return of(this.router.createUrlTree(['dashboard']));
        }

        return iif(() => {
          return this.cookieService.check('adminAccessToken');
        }, this.authService.getAdminUserInfo(), of(orgUser));
      }),
      map((result) => {
        if(result instanceof UrlTree) {
          return result;
        }
        return true;
      }),
      catchError(({ status, error: { errorCode } }) => {
        if(status === 500) {
          return of(false);
        }

        switch(errorCode) {
          case "InvalidAccessToken":
          case "Signup":
            return of(false);
        }

        if(errorCode === "Blocked") {
          const moddalRef = this.modalLoginService.open();
          return moddalRef.onClose.pipe(
            first(),
            switchMap(() => {
              const p = this.navigateByUrl(this.authService.url);
              return from(p);
            })
          );
        }

        if(errorCode === "Closed") {
          const moddalRef = this.modalLoginService.open();
          return moddalRef.onClose.pipe(
            first(),
            switchMap(() => {
              const p = this.navigateByUrl(this.authService.url);
              return from(p);
            })
          );
        }

        return of(this.router.createUrlTree([this.authService.url]));
      })
    );
  }

  navigateByUrl(url: string) {
    return this.router.navigate(['empty-page'], {
      skipLocationChange: true
    }).then(()=> {
      return this.router.navigateByUrl(url);
    });
  }
  
}
